import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { challengeAPI, fieldAPI, teamAPI } from '../lib/api';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { 
  Swords, 
  Plus, 
  Calendar,
  MapPin,
  Clock,
  Users,
  Check,
  X,
  Trophy,
  Loader2,
  Banknote,
  ChevronRight
} from 'lucide-react';
import { formatCurrency, generateInitials } from '../lib/utils';

export default function Challenges() {
  const { user } = useAuth();
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [availableTeams, setAvailableTeams] = useState([]);
  const [availableFields, setAvailableFields] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [myTeam, setMyTeam] = useState(null);
  const [processing, setProcessing] = useState(null);
  
  const [newChallenge, setNewChallenge] = useState({
    opponent_team_id: '',
    field_id: '',
    date: new Date().toISOString().split('T')[0],
    time: '',
    duration_hours: 2,
    message: ''
  });

  useEffect(() => {
    fetchChallenges();
    fetchMyTeam();
  }, []);

  const fetchChallenges = async () => {
    try {
      const { data } = await challengeAPI.getAll();
      setChallenges(data);
    } catch (error) {
      console.error('Error fetching challenges:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyTeam = async () => {
    try {
      const { data } = await teamAPI.getMy();
      // Find team where user is captain
      const captainTeam = data.find(t => t.captain_id === user?.id);
      setMyTeam(captainTeam);
    } catch (error) {
      console.error('Error fetching my team:', error);
    }
  };

  const fetchAvailableTeams = async () => {
    try {
      const { data } = await challengeAPI.getAvailableTeams();
      setAvailableTeams(data);
    } catch (error) {
      toast.error('Fehler beim Laden der Teams');
    }
  };

  const fetchAvailableFields = async (date) => {
    try {
      const { data } = await fieldAPI.getAvailable(date);
      setAvailableFields(data);
    } catch (error) {
      toast.error('Fehler beim Laden der Felder');
    }
  };

  const fetchAvailableSlots = async (fieldId, date) => {
    if (!fieldId || !date) {
      setAvailableSlots([]);
      return;
    }
    
    setLoadingSlots(true);
    try {
      const { data } = await fieldAPI.getAvailability(fieldId, date);
      const slots = data.slots || [];
      const available = slots.filter(s => s.status === 'available');
      setAvailableSlots(available);
      
      // Reset time if no longer available
      if (newChallenge.time && !available.find(s => s.start_time === newChallenge.time)) {
        setNewChallenge(prev => ({ ...prev, time: '' }));
      }
    } catch (error) {
      console.error('Error fetching availability:', error);
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleFieldChange = (fieldId) => {
    setNewChallenge(prev => ({ ...prev, field_id: fieldId, time: '' }));
    fetchAvailableSlots(fieldId, newChallenge.date);
  };

  const handleOpenCreate = () => {
    if (!myTeam) {
      toast.error('Du musst Kapitän eines Teams sein um eine Herausforderung zu erstellen');
      return;
    }
    fetchAvailableTeams();
    fetchAvailableFields(newChallenge.date);
    setCreateOpen(true);
  };

  const handleDateChange = (date) => {
    setNewChallenge(prev => ({ ...prev, date, time: '' }));
    setAvailableSlots([]);
    fetchAvailableFields(date);
    if (newChallenge.field_id) {
      fetchAvailableSlots(newChallenge.field_id, date);
    }
  };

  const handleCreateChallenge = async (e) => {
    e.preventDefault();
    
    if (!newChallenge.opponent_team_id || !newChallenge.field_id) {
      toast.error('Bitte wähle ein Team und ein Feld aus');
      return;
    }

    setCreating(true);
    try {
      await challengeAPI.create(newChallenge);
      toast.success('Herausforderung gesendet!');
      setCreateOpen(false);
      setNewChallenge({
        opponent_team_id: '',
        field_id: '',
        date: new Date().toISOString().split('T')[0],
        time: '18:00',
        duration_hours: 2,
        message: ''
      });
      await fetchChallenges();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen');
    } finally {
      setCreating(false);
    }
  };

  const handleAccept = async (challengeId) => {
    setProcessing(challengeId);
    try {
      await challengeAPI.accept(challengeId);
      toast.success('Herausforderung angenommen!');
      await fetchChallenges();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler');
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (challengeId) => {
    setProcessing(challengeId);
    try {
      await challengeAPI.reject(challengeId);
      toast.success('Herausforderung abgelehnt');
      await fetchChallenges();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler');
    } finally {
      setProcessing(null);
    }
  };

  const handleCancel = async (challengeId) => {
    setProcessing(challengeId);
    try {
      await challengeAPI.cancel(challengeId);
      toast.success('Herausforderung abgebrochen');
      await fetchChallenges();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler');
    } finally {
      setProcessing(null);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-amber-100 text-amber-700">Ausstehend</Badge>;
      case 'accepted':
        return <Badge className="bg-green-100 text-green-700">Angenommen</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-700">Abgelehnt</Badge>;
      case 'cancelled':
        return <Badge className="bg-gray-100 text-gray-700">Abgebrochen</Badge>;
      case 'completed':
        return <Badge className="bg-blue-100 text-blue-700">Abgeschlossen</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const isChallenger = (challenge) => challenge.challenger_captain_id === user?.id;
  const isOpponent = (challenge) => challenge.opponent_captain_id === user?.id;

  const pendingChallenges = challenges.filter(c => c.status === 'pending');
  const activeChallenges = challenges.filter(c => c.status === 'accepted');
  const pastChallenges = challenges.filter(c => ['rejected', 'cancelled', 'completed'].includes(c.status));

  if (loading) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 py-8">
          <Skeleton className="h-10 w-64 mb-6" />
          <div className="grid gap-4">
            {[1, 2, 3].map(i => <Skeleton key={i} className="h-40" />)}
          </div>
        </div>
      </Layout>
    );
  }

  const ChallengeCard = ({ challenge }) => {
    const isUserChallenger = isChallenger(challenge);
    const isUserOpponent = isOpponent(challenge);
    const canRespond = isUserOpponent && challenge.status === 'pending';
    const canCancel = isUserChallenger && ['pending', 'accepted'].includes(challenge.status);

    return (
      <Card className="border-slate-200 hover:shadow-md transition-shadow">
        <CardContent className="p-5">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-4">
              {/* Challenger Team */}
              <div className="text-center">
                <Avatar className="h-14 w-14 mx-auto mb-1 border-2 border-tunis-blue/20">
                  <AvatarFallback className="bg-tunis-blue text-white font-bold">
                    {generateInitials(challenge.challenger_team_name)}
                  </AvatarFallback>
                </Avatar>
                <p className="text-sm font-medium truncate max-w-[80px]">{challenge.challenger_team_name}</p>
                {isUserChallenger && <Badge className="text-xs bg-tunis-blue/10 text-tunis-blue mt-1">Dein Team</Badge>}
              </div>

              {/* VS */}
              <div className="flex flex-col items-center px-4">
                <Swords className="w-8 h-8 text-amber-500 mb-1" />
                <span className="text-xs font-bold text-muted-foreground">VS</span>
              </div>

              {/* Opponent Team */}
              <div className="text-center">
                <Avatar className="h-14 w-14 mx-auto mb-1 border-2 border-tunis-teal/20">
                  <AvatarFallback className="bg-tunis-teal text-white font-bold">
                    {generateInitials(challenge.opponent_team_name)}
                  </AvatarFallback>
                </Avatar>
                <p className="text-sm font-medium truncate max-w-[80px]">{challenge.opponent_team_name}</p>
                {isUserOpponent && <Badge className="text-xs bg-tunis-teal/10 text-tunis-teal mt-1">Dein Team</Badge>}
              </div>
            </div>

            {getStatusBadge(challenge.status)}
          </div>

          {/* Details */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4 text-sm">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Calendar className="w-4 h-4 text-tunis-blue" />
              <span>{new Date(challenge.date).toLocaleDateString('de-DE', { weekday: 'short', day: 'numeric', month: 'short' })}</span>
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">
              <Clock className="w-4 h-4 text-tunis-blue" />
              <span>{challenge.time} Uhr</span>
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">
              <MapPin className="w-4 h-4 text-tunis-teal" />
              <span className="truncate">{challenge.field_name}</span>
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">
              <Banknote className="w-4 h-4 text-amber-500" />
              <span>{formatCurrency(challenge.price_per_team)}/Team</span>
            </div>
          </div>

          {challenge.message && (
            <p className="text-sm text-muted-foreground italic mb-4 bg-slate-50 p-3 rounded-lg">
              "{challenge.message}"
            </p>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2">
            {canRespond && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-red-600 hover:bg-red-50"
                  onClick={() => handleReject(challenge.id)}
                  disabled={processing === challenge.id}
                >
                  <X className="w-4 h-4 mr-1" />
                  Ablehnen
                </Button>
                <Button
                  size="sm"
                  className="bg-tunis-teal hover:bg-tunis-teal/90"
                  onClick={() => handleAccept(challenge.id)}
                  disabled={processing === challenge.id}
                >
                  {processing === challenge.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-1" />
                      Annehmen
                    </>
                  )}
                </Button>
              </>
            )}
            {canCancel && (
              <Button
                variant="outline"
                size="sm"
                className="text-red-600 hover:bg-red-50"
                onClick={() => handleCancel(challenge.id)}
                disabled={processing === challenge.id}
              >
                Abbrechen
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 py-8 pb-24">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-display font-bold text-2xl text-tunis-blue flex items-center gap-3">
              <Swords className="w-8 h-8" />
              Team-Herausforderungen
            </h1>
            <p className="text-muted-foreground">Fordere andere Teams zu Freundschaftsspielen heraus</p>
          </div>
          
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="btn-primary" onClick={handleOpenCreate}>
                <Plus className="w-5 h-5 mr-2" />
                Herausforderung
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Team herausfordern</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateChallenge} className="space-y-4 pt-4">
                {myTeam && (
                  <div className="p-3 bg-tunis-blue/5 rounded-lg">
                    <p className="text-sm text-muted-foreground">Dein Team:</p>
                    <p className="font-semibold text-tunis-blue">{myTeam.name}</p>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Gegnerisches Team</Label>
                  <Select
                    value={newChallenge.opponent_team_id}
                    onValueChange={(value) => setNewChallenge(prev => ({ ...prev, opponent_team_id: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Team auswählen..." />
                    </SelectTrigger>
                    <SelectContent>
                      {availableTeams.map(team => (
                        <SelectItem key={team.id} value={team.id}>
                          <div className="flex items-center gap-2">
                            <span>{team.name}</span>
                            <Badge className="text-xs">{team.team_elo} ELO</Badge>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Datum</Label>
                  <Input
                    type="date"
                    value={newChallenge.date}
                    onChange={(e) => handleDateChange(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Spielfeld</Label>
                  <Select
                    value={newChallenge.field_id}
                    onValueChange={handleFieldChange}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Feld auswählen..." />
                    </SelectTrigger>
                    <SelectContent>
                      {availableFields.map(field => (
                        <SelectItem key={field.id} value={field.id}>
                          <div className="flex items-center justify-between gap-4">
                            <span>{field.name}</span>
                            <span className="text-muted-foreground">{formatCurrency(field.price_per_hour)}/h</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Uhrzeit</Label>
                  <Select
                    value={newChallenge.time}
                    onValueChange={(value) => setNewChallenge(prev => ({ ...prev, time: value }))}
                    disabled={!newChallenge.field_id || loadingSlots}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={
                        loadingSlots ? "Lädt..." :
                        !newChallenge.field_id ? "Erst Feld wählen" :
                        availableSlots.length === 0 ? "Keine verfügbar" :
                        "Zeit wählen"
                      } />
                    </SelectTrigger>
                    <SelectContent>
                      {availableSlots.length === 0 ? (
                        <div className="p-2 text-sm text-muted-foreground text-center">
                          Keine verfügbaren Zeiten
                        </div>
                      ) : (
                        availableSlots.map((slot) => (
                          <SelectItem key={slot.start_time} value={slot.start_time}>
                            <div className="flex items-center gap-2">
                              <Clock className="w-4 h-4 text-tunis-teal" />
                              {slot.start_time} - {slot.end_time}
                            </div>
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  {newChallenge.field_id && availableSlots.length === 0 && !loadingSlots && (
                    <p className="text-xs text-amber-600">
                      Alle Zeiten sind blockiert oder gebucht
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Dauer</Label>
                  <Select
                    value={newChallenge.duration_hours.toString()}
                    onValueChange={(value) => setNewChallenge(prev => ({ ...prev, duration_hours: parseFloat(value) }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 Stunde (60 Min)</SelectItem>
                      <SelectItem value="1.5">1,5 Stunden (90 Min)</SelectItem>
                      <SelectItem value="2">2 Stunden (120 Min)</SelectItem>
                      <SelectItem value="2.5">2,5 Stunden (150 Min)</SelectItem>
                      <SelectItem value="3">3 Stunden (180 Min)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Nachricht (optional)</Label>
                  <Textarea
                    value={newChallenge.message}
                    onChange={(e) => setNewChallenge(prev => ({ ...prev, message: e.target.value }))}
                    placeholder="z.B. Freundschaftsspiel zur Vorbereitung..."
                    rows={2}
                  />
                </div>

                {newChallenge.field_id && (
                  <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <p className="text-sm font-medium text-amber-800">
                      💰 Kosten: {formatCurrency((availableFields.find(f => f.id === newChallenge.field_id)?.price_per_hour || 0) * newChallenge.duration_hours / 2)} pro Team (50/50)
                    </p>
                  </div>
                )}

                <Button type="submit" className="w-full btn-primary" disabled={creating}>
                  {creating ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Herausforderung senden'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="pending" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="pending" className="gap-2">
              Ausstehend
              {pendingChallenges.length > 0 && (
                <Badge className="bg-amber-500 text-white">{pendingChallenges.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="active">Angenommen ({activeChallenges.length})</TabsTrigger>
            <TabsTrigger value="past">Vergangene ({pastChallenges.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="pending" className="space-y-4">
            {pendingChallenges.length === 0 ? (
              <Card className="border-slate-200">
                <CardContent className="p-12 text-center">
                  <Swords className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                  <h3 className="font-medium text-lg mb-2">Keine ausstehenden Herausforderungen</h3>
                  <p className="text-muted-foreground mb-4">
                    Fordere ein anderes Team zu einem Freundschaftsspiel heraus!
                  </p>
                </CardContent>
              </Card>
            ) : (
              pendingChallenges.map(challenge => (
                <ChallengeCard key={challenge.id} challenge={challenge} />
              ))
            )}
          </TabsContent>

          <TabsContent value="active" className="space-y-4">
            {activeChallenges.length === 0 ? (
              <Card className="border-slate-200">
                <CardContent className="p-12 text-center">
                  <Trophy className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                  <h3 className="font-medium text-lg mb-2">Keine angenommenen Herausforderungen</h3>
                  <p className="text-muted-foreground">
                    Angenommene Herausforderungen erscheinen hier.
                  </p>
                </CardContent>
              </Card>
            ) : (
              activeChallenges.map(challenge => (
                <ChallengeCard key={challenge.id} challenge={challenge} />
              ))
            )}
          </TabsContent>

          <TabsContent value="past" className="space-y-4">
            {pastChallenges.length === 0 ? (
              <Card className="border-slate-200">
                <CardContent className="p-12 text-center">
                  <Calendar className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                  <h3 className="font-medium text-lg mb-2">Keine vergangenen Herausforderungen</h3>
                  <p className="text-muted-foreground">
                    Vergangene und abgeschlossene Herausforderungen erscheinen hier.
                  </p>
                </CardContent>
              </Card>
            ) : (
              pastChallenges.map(challenge => (
                <ChallengeCard key={challenge.id} challenge={challenge} />
              ))
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
